import {
  Application,
  PipelineMode,
  StackStatus,
} from "src/domains/application/types";

export interface ConfigValidations {
  hasConfig: boolean;
  hasVcsRepo: boolean;
  notDeleted: boolean;
  billingReady: boolean;
}

export interface FailedStep {
  phase: string;
  message: string;
}

interface UseStackValidationReturn {
  // Status booleans
  canResume: boolean;
  canPause: boolean;
  canDeploy: boolean;
  isProvisioning: boolean;
  isFailed: boolean;
  isProvisioningFailed: boolean;
  isConfigured: boolean;
  needsConfigure: boolean;
  configReady: boolean;
  configValidations: ConfigValidations;

  // Deploy mode flags
  isCiManaged: boolean;
  isCiBuildRushaDeploy: boolean;
  isCiControlled: boolean;
  isRushaBuildCiDeploy: boolean;
  isRushaOwnedBuild: boolean;

  // Failure detail
  failedStep: FailedStep | null;
}

const DEPLOYMENT_ELIGIBLE_STATUSES: StackStatus[] = [
  StackStatus.RUNNING,
  StackStatus.STOPPED,
  StackStatus.DRAFT,
  StackStatus.VCS_PROVISIONED,
  StackStatus.FAILED,
  StackStatus.PROVISIONING_FAILED,
];

// The shape returned by usePipelineStatus
type PipelineSteps = Record<
  "vcs" | "build" | "deploy",
  { status: string; message: string } | null
>;

export function useStackValidation(
  system: Application | null,
  orgBillingQuotaExhausted: boolean | undefined,
  pipelineSteps?: PipelineSteps,
): UseStackValidationReturn {
  if (!system) {
    return {
      canResume: false,
      canPause: false,
      canDeploy: false,
      isProvisioning: false,
      isFailed: false,
      isProvisioningFailed: false,
      isConfigured: false,
      needsConfigure: true,
      configReady: false,
      configValidations: {
        hasConfig: false,
        hasVcsRepo: false,
        notDeleted: true,
        billingReady: true,
      },
      isCiManaged: false,
      isCiBuildRushaDeploy: false,
      isCiControlled: false,
      isRushaBuildCiDeploy: false,
      isRushaOwnedBuild: false,
      failedStep: null,
    };
  }

  const canResume = system.status === StackStatus.STOPPED;
  const canPause = system.status === StackStatus.RUNNING;

  // For adopted stacks, the inspector already detected the repo's services.
  // Those are stored in stackConfig._detectedServices and count as configured.
  const stackConfig = (system.stackConfig ?? {}) as Record<string, unknown>;
  const hasDetectedServices =
    Array.isArray(stackConfig._detectedServices) &&
    stackConfig._detectedServices.length > 0;

  const isConfigured = Boolean(system.templateSlug || hasDetectedServices);

  const configValidations: ConfigValidations = {
    hasConfig: isConfigured,
    hasVcsRepo: Boolean(system.vcsRepoUrl),
    notDeleted: system.status !== StackStatus.DELETED,
    billingReady: !orgBillingQuotaExhausted,
  };

  const configReady = Object.values(configValidations).every(Boolean);
  const isProvisioning = system.status === StackStatus.PROVISIONING;
  const isFailed = system.status === StackStatus.FAILED;
  const isProvisioningFailed =
    system.status === StackStatus.PROVISIONING_FAILED;

  const canDeploy =
    configReady &&
    !isProvisioning &&
    DEPLOYMENT_ELIGIBLE_STATUSES.includes(system.status);

  // Exactly two ownership models exist (P1-019): rusha_managed and
  // ci_build_rusha_deploy. Legacy modes are no longer creatable; the legacy
  // flags remain for consumers that render historical stack data.
  const isCiManaged: boolean = false;
  const isCiBuildRushaDeploy: boolean =
    system.pipelineMode === PipelineMode.CI_BUILD_RUSHA_DEPLOY;
  const isCiControlled: boolean = isCiBuildRushaDeploy;
  const isRushaBuildCiDeploy: boolean = false;
  const isRushaOwnedBuild: boolean =
    system.pipelineMode === PipelineMode.RUSHA_MANAGED;

  const needsConfigure = !configValidations.hasConfig;

  // Determine which step failed (if any) for more specific alert messages
  const failedStep: FailedStep | null = isFailed
    ? pipelineSteps?.build?.status === "failed"
      ? { phase: "Build", message: pipelineSteps.build.message }
      : pipelineSteps?.deploy?.status === "failed"
        ? { phase: "Deploy", message: pipelineSteps.deploy.message }
        : null
    : null;

  return {
    canResume,
    canPause,
    canDeploy,
    isProvisioning,
    isFailed,
    isProvisioningFailed,
    isConfigured,
    needsConfigure,
    configReady,
    configValidations,
    isCiManaged,
    isCiBuildRushaDeploy,
    isCiControlled,
    isRushaBuildCiDeploy,
    isRushaOwnedBuild,
    failedStep,
  };
}

export default useStackValidation;
